import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { Component, useCallback, useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes, css } from 'styled-components';
import { g as darken, m as media } from './media-15f49f27.js';
import { C as CloseIconSVG } from './CrossSolid-52de5c52.js';

class BlurFilter extends Component {
  static defaultProps = {
    blurAmount: 6
  };

  componentDidMount() {
    const oldTempStyle = document.getElementById('blur-css');
    if (oldTempStyle) return;
    const {
      filterId,
      blurAmount
    } = this.props;
    /*
        Create a temporary <style> tag
        */

    const cssContent = `
			#${filterId} {
				filter: url(#blur-filter);
				filter: blur(${blurAmount}px);
			}
		`;

    if (document) {
      const tempStyle = document.createElement('style');
      tempStyle.type = 'text/css';
      tempStyle.id = 'blur-css';
      tempStyle.appendChild(document.createTextNode(cssContent));
      document.head && document.head.appendChild(tempStyle);
    }
  }

  componentWillUnmount() {
    if (document) {
      const tempStyle = document.getElementById('blur-css');

      if (tempStyle && document.head) {
        // possible edge case where there was an existing
        // 'filter' value on the target element
        document.head.removeChild(tempStyle);
      }
    }
  }

  render() {
    const {
      blurAmount
    } = this.props;
    return jsx("svg", {
      xmlns: "http://www.w3.org/2000/svg",
      version: "1.1",
      width: "0",
      height: "0",
      children: jsx("defs", {
        children: jsx("filter", {
          id: "blur-filter",
          x: "0%",
          y: "0%",
          width: "100%",
          height: "100%",
          colorInterpolationFilters: "sRGB",
          children: jsx("feGaussianBlur", {
            in: "SourceGraphic",
            stdDeviation: blurAmount
          })
        })
      })
    });
  }

}

const loadingKeyframes = keyframes`
	from 				{transform: rotate(0);}
	to 					{transform: rotate(360deg);}
`;
const loadingAnimation = function (strings) {
  for (var _len = arguments.length, interpolations = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    interpolations[_key - 1] = arguments[_key];
  }

  return css`
  &:before {
    content: '';
    display: block;
    opacity: 1;
    transition: opacity 0.2s;
    width: ${_ref => {
    let {
      width,
      theme
    } = _ref;
    return width ? width : theme.x2spacePx;
  }}px;
    height: ${_ref2 => {
    let {
      width,
      theme
    } = _ref2;
    return width ? width : theme.x2spacePx;
  }}px;
    border-radius: ${_ref3 => {
    let {
      width,
      theme
    } = _ref3;
    return width ? width : theme.x2spacePx;
  }}px;
    border: solid 1px
      ${_ref4 => {
    let {
      light,
      styleType,
      theme
    } = _ref4;
    return light === true || styleType === 'light' ? `#fff` : theme.color.black;
  }};
    border-bottom-color: transparent;
    animation: ${loadingKeyframes} 0.5s linear infinite forwards;
    ${css(strings, ...interpolations)};
  }
`;
};
const LoadingSpinner = styled('div')`
  position: ${_ref5 => {
  let {
    top,
    right,
    bottom,
    left
  } = _ref5;
  return top || right || bottom || left ? `absolute` : `static`;
}};
  top: ${_ref6 => {
  let {
    top
  } = _ref6;
  return top ? top : `auto`;
}};
  right: ${_ref7 => {
  let {
    right
  } = _ref7;
  return right ? right : `auto`;
}};
  bottom: ${_ref8 => {
  let {
    bottom
  } = _ref8;
  return bottom ? bottom : `auto`;
}};
  left: ${_ref9 => {
  let {
    left
  } = _ref9;
  return left ? left : `auto`;
}};
  margin-top: ${_ref10 => {
  let {
    top,
    width,
    theme
  } = _ref10;
  return top ? `-${(width || theme.x2spacePx) / 2}px` : `0`;
}};
  margin-left: ${_ref11 => {
  let {
    left,
    width,
    theme
  } = _ref11;
  return left ? `-${(width || theme.x2spacePx) / 2}px` : `0`;
}};
  margin-bottom: ${_ref12 => {
  let {
    bottom,
    width,
    theme
  } = _ref12;
  return bottom ? `-${(width || theme.x2spacePx) / 2}px` : `0`;
}};
  margin-right: ${_ref13 => {
  let {
    right,
    width,
    theme
  } = _ref13;
  return right ? `-${(width || theme.x2spacePx) / 2}px` : `0`;
}};
  z-index: ${_ref14 => {
  let {
    zIndex
  } = _ref14;
  return zIndex ? zIndex : 0;
}};
  ${loadingAnimation``};
`;

const displayKeys = ['displayXsDown', 'displaySmDown', 'displayMdDown', 'displayLgDown', 'displaySmUp', 'displayMdUp', 'displayLgUp', 'displayXlUp'];
const displayMap = {
  displayXsDown: 'xsDown',
  displaySmDown: 'smDown',
  displayMdDown: 'mdDown',
  displayLgDown: 'lgDown',
  displaySmUp: 'smUp',
  displayMdUp: 'mdUp',
  displayLgUp: 'lgUp',
  displayXlUp: 'xlUp'
};

const getPadding = theme => theme.x2spacePx - 4;

const font = css`
  ${_ref => {
  let {
    theme,
    styleType
  } = _ref;

  switch (styleType) {
    case 'passive':
    case 'passiveReverse':
    case 'gentle':
    case 'gentleReverse':
    case 'solid':
    case 'solidReverse':
    case 'loud':
    case 'louder':
      return theme.typography.tiny;

    case 'gentleLarge':
    case 'gentleLargeReverse':
    case 'solidLarge':
    case 'solidLargeReverse':
    case 'passiveLarge':
    case 'passiveLargeReverse':
    case 'loudLarge':
    case 'louderLarge':
      return theme.typography.xSmall;

    default:
      return 'font-size: inherit';
  }
}}
`;
const lineHeight = css`
  line-height: ${_ref2 => {
  let {
    theme,
    styleType
  } = _ref2;

  switch (styleType) {
    case 'passive':
    case 'passiveReverse':
    case 'gentle':
    case 'gentleReverse':
    case 'solid':
    case 'solidReverse':
    case 'loud':
    case 'louder':
      return `${theme.x4spacePx}px`;

    case 'gentleLarge':
    case 'gentleLargeReverse':
    case 'solidLarge':
    case 'solidLargeReverse':
    case 'passiveLarge':
    case 'passiveLargeReverse':
    case 'loudLarge':
    case 'louderLarge':
      return `${theme.x5spacePx}px`;

    default:
      return 'inherit';
  }
}};
`;
const height = css`
  height: ${_ref3 => {
  let {
    theme,
    styleType
  } = _ref3;

  switch (styleType) {
    case 'passive':
    case 'passiveReverse':
    case 'gentle':
    case 'gentleReverse':
    case 'solid':
    case 'solidReverse':
    case 'loud':
    case 'louder':
      return `${theme.x4spacePx}px`;

    case 'gentleLarge':
    case 'gentleLargeReverse':
    case 'solidLarge':
    case 'solidLargeReverse':
    case 'passiveLarge':
    case 'passiveLargeReverse':
    case 'loudLarge':
    case 'louderLarge':
      return `${theme.x5spacePx}px`;

    default:
      return 'auto';
  }
}};
`;
const padding = css`
  padding: ${_ref4 => {
  let {
    theme,
    styleType
  } = _ref4;

  if (styleType === 'softLink' || styleType === 'strongLink' || styleType === 'softLinkReverse') {
    return '0';
  }

  return `0 ${getPadding(theme)}px`;
}};
`;
const color = css`
  color: ${_ref5 => {
  let {
    theme,
    styleType
  } = _ref5;

  switch (styleType) {
    case 'strongLink':
    case 'loud':
    case 'loudLarge':
      return theme.color.brand01;

    case 'gentleReverse':
    case 'gentleLargeReverse':
    case 'solid':
    case 'solidLarge':
      return theme.color.spaceGray;

    case 'softLinkReverse':
      return theme.color.darkGray;

    case 'passive':
    case 'passiveLarge':
      return theme.color.whiteRGBA(0.7);

    case 'passiveReverse':
    case 'passiveLargeReverse':
      return theme.color.spaceGrayRGBA(0.5);

    default:
      return theme.color.white;
  }
}};
`;
const backgroundColor = css`
  background-color: ${_ref6 => {
  let {
    theme,
    styleType
  } = _ref6;

  switch (styleType) {
    case 'loud':
    case 'loudLarge':
      return theme.color.brand03RGBA(0.1);

    case 'louder':
    case 'louderLarge':
      return theme.color.brand01;

    case 'solid':
    case 'solidLarge':
      return theme.color.white;

    case 'solidReverse':
    case 'solidLargeReverse':
      return theme.color.spaceGray;

    default:
      return 'transparent';
  }
}};
`;
const boxShadow = css`
  box-shadow: ${_ref7 => {
  let {
    theme,
    styleType
  } = _ref7;

  switch (styleType) {
    case 'gentle':
    case 'gentleLarge':
      return `inset 0px 0px 0 1px ${theme.color.whiteRGBA(0.2)}`;

    case 'gentleReverse':
    case 'gentleLargeReverse':
      return `inset 0px 0px 0 1px ${theme.color.spaceGrayRGBA(0.2)}`;

    case 'softLink':
      return `inset 0px -1px 0 0 ${theme.color.whiteRGBA(0.5)}`;

    case 'softLinkReverse':
      return `inset 0px -1px 0 0 ${theme.color.spaceGrayRGBA(0.3)}`;

    case 'strongLink':
      return `inset 0px -1px 0 0 ${theme.color.brand01RGBA(0.5)}`;

    default:
      return 'none';
  }
}};
`;
const hover = css`
  background-color: ${_ref8 => {
  let {
    theme,
    styleType
  } = _ref8;

  switch (styleType) {
    case 'loud':
    case 'loudLarge':
      return theme.color.brand03RGBA(0.2);

    case 'passive':
    case 'passiveLarge':
      return theme.color.whiteRGBA(0.05);

    case 'passiveReverse':
    case 'passiveLargeReverse':
      return theme.color.spaceGrayRGBA(0.05);

    case 'gentle':
    case 'gentleLarge':
      return theme.color.whiteRGBA(0.1);

    case 'gentleReverse':
    case 'gentleLargeReverse':
      return theme.color.spaceGrayRGBA(0.1);

    case 'louder':
    case 'louderLarge':
      return `rgb(${darken(theme.color.brand01RGB, 0.1)})`;

    case 'solid':
    case 'solidLarge':
      return `rgb(${darken(theme.color.whiteRGB, 0.1)})`;

    case 'solidReverse':
    case 'solidLargeReverse':
      return theme.color.spaceGrayRGBA(0.9);

    default:
      return 'transparent';
  }
}};
  box-shadow: ${_ref9 => {
  let {
    theme,
    styleType
  } = _ref9;

  switch (styleType) {
    case 'gentle':
    case 'gentleLarge':
      return `inset 0px 0px 0 1px ${theme.color.white}`;

    case 'gentleReverse':
    case 'gentleLargeReverse':
      return `inset 0px 0px 0 1px ${theme.color.spaceGrayRGBA(0.7)}`;

    case 'softLink':
      return `inset 0px -1px 0 0 ${theme.color.white}`;

    case 'softLinkReverse':
      return `inset 0px -1px 0 0 ${theme.color.spaceGrayRGBA(0.7)}`;

    case 'strongLink':
      return `inset 0px -1px 0 0 ${theme.color.brand01}`;

    default:
      return 'none';
  }
}};
`;
const active = css`
  background-color: ${_ref10 => {
  let {
    theme,
    styleType
  } = _ref10;

  switch (styleType) {
    case 'loud':
    case 'loudLarge':
      return theme.color.brand03RGBA(0.2);

    case 'passive':
    case 'passiveLarge':
      return theme.color.whiteRGBA(0.1);

    case 'passiveReverse':
    case 'passiveLargeReverse':
      return theme.color.spaceGrayRGBA(0.1);

    case 'gentle':
    case 'gentleLarge':
      return theme.color.whiteRGBA(0.2);

    case 'gentleReverse':
    case 'gentleLargeReverse':
      return theme.color.spaceGrayRGBA(0.2);

    case 'louder':
    case 'louderLarge':
      return `rgb(${darken(theme.color.brand01RGB, 0.2)})`;

    case 'solid':
    case 'solidLarge':
      return `rgb(${darken(theme.color.whiteRGB, 0.2)})`;

    case 'solidReverse':
    case 'solidLargeReverse':
      return theme.color.spaceGrayRGBA(0.8);

    default:
      return 'transparent';
  }
}};
  box-shadow: ${_ref11 => {
  let {
    theme,
    styleType
  } = _ref11;

  switch (styleType) {
    case 'gentle':
    case 'gentleLarge':
      return `inset 0px 0px 0 1px ${theme.color.white}`;

    case 'gentleReverse':
    case 'gentleLargeReverse':
      return `inset 0px 0px 0 1px ${theme.color.spaceGrayRGBA(0.5)}`;

    case 'softLink':
      return `inset 0px -1px 0 0 ${theme.color.whiteRGBA(0.8)}`;

    case 'softLinkReverse':
      return `inset 0px -1px 0 0 ${theme.color.spaceGrayRGBA(0.5)}`;

    case 'strongLink':
      return `inset 0px -1px 0 0 ${theme.color.brand01RGBA(0.8)}`;

    default:
      return 'none';
  }
}};
`;
const disabled = css`
  cursor: auto;
  pointer-events: none;
  opacity: 0.3;
  color: ${_ref12 => {
  let {
    theme,
    styleType
  } = _ref12;

  switch (styleType) {
    case 'strongLink':
    case 'loud':
    case 'loudLarge':
      return theme.color.brand01;

    case 'gentleReverse':
    case 'gentleLargeReverse':
    case 'solid':
    case 'solidLarge':
      return theme.color.spaceGray;

    case 'softLinkReverse':
      return theme.color.darkGray;

    case 'passive':
    case 'passiveLarge':
      return theme.color.whiteRGBA(0.7);

    case 'passiveReverse':
    case 'passiveLargeReverse':
      return theme.color.spaceGrayRGBA(0.5);

    default:
      return theme.color.white;
  }
}};
  box-shadow: ${_ref13 => {
  let {
    theme,
    styleType
  } = _ref13;

  switch (styleType) {
    case 'strongLink':
    case 'softLink':
      return `inset 0px -1px 0 0 ${theme.color.whiteRGBA(0.3)}`;

    case 'gentle':
    case 'gentleLarge':
      return `inset 0px 0px 0 1px ${theme.color.whiteRGBA(0.3)}`;

    case 'gentleReverse':
    case 'gentleLargeReverse':
      return `inset 0px 0px 0 1px ${theme.color.spaceGrayRGBA(0.5)}`;

    default:
      return 'none';
  }
}};
  background-color: 'transparent';
`;

const hasDarkSpinner = styleType => {
  switch (styleType) {
    case 'passiveReverse':
    case 'gentleReverse':
    case 'gentleLargeReverse':
    case 'solid':
    case 'solidLarge':
      return true;

    default:
      return false;
  }
};

const loadingAnimationStyles = css`
  position: absolute;
  top: 50%;
  right: 12px;
  margin-top: -${_ref14 => {
  let {
    theme
  } = _ref14;
  return theme.x2spacePx / 2;
}}px;
`;
const loading = css`
  ${_ref15 => {
  let {
    isLoading
  } = _ref15;
  return isLoading && loadingAnimation`
    ${loadingAnimationStyles}
    border-color: white;
    border-bottom-color: transparent;
    `;
}}
`;
const loadingBlack = css`
  ${_ref16 => {
  let {
    isLoading
  } = _ref16;
  return isLoading && loadingAnimation`
      ${loadingAnimationStyles}
      border-color: black;
      border-bottom-color: transparent;
    `;
}}
`;
const horizontalMargin = css`
  ${_ref17 => {
  let {
    marginLeft,
    marginRight,
    theme
  } = _ref17;
  const marginLeftStyle = `margin-left: ${theme.x1spacePx / 2}px;`;
  const marginRightStyle = `margin-right: ${theme.x1spacePx / 2}px;`;

  if (marginLeft) {
    return marginLeftStyle;
  }

  if (marginRight) {
    return marginRightStyle;
  }
}};
`;
const verticalMargin = css`
  ${_ref18 => {
  let {
    marginTop,
    marginBottom,
    theme
  } = _ref18;

  if (marginTop) {
    return `margin-top: ${theme.x1spacePx / 2}px;`;
  }

  if (marginBottom) {
    return `margin-bottom: ${theme.x1spacePx / 2}px;`;
  }
}};
`;

const boxCSS = boxType => css`
  display: ${boxType};
  width: ${boxType === 'block' ? '100%' : 'auto'};
  ${_ref19 => {
  let {
    alignIcon
  } = _ref19;
  return alignIcon && `text-align: left;`;
}}
`;

const box = css`
  ${_ref20 => {
  let {
    display,
    displayXsDown,
    displaySmDown,
    displayMdDown,
    displayLgDown,
    displaySmUp,
    displayMdUp,
    displayLgUp,
    displayXlUp
  } = _ref20;

  if (typeof display !== 'undefined') {
    return boxCSS(display);
  }

  const entities = {
    displayXsDown,
    displaySmDown,
    displayMdDown,
    displayLgDown,
    displaySmUp,
    displayMdUp,
    displayLgUp,
    displayXlUp
  };
  return css`
      ${displayKeys.filter(key => typeof entities[key] !== 'undefined').map(key => {
    return media[displayMap[key]]`
              ${boxCSS(entities[key])}
          `;
  })}
    `;
}};
`;
const svgFill = css`
  fill: ${_ref21 => {
  let {
    theme,
    styleType
  } = _ref21;

  switch (styleType) {
    case 'loud':
    case 'loudLarge':
      return theme.color.brand01;

    case 'louder':
    case 'louderLarge':
    case 'gentle':
    case 'gentleLarge':
    case 'passive':
    case 'passiveLarge':
      return theme.color.white;

    case 'gentleReverse':
    case 'gentleLargeReverse':
    case 'solid':
    case 'solidLarge':
    case 'passiveReverse':
    case 'passiveLargeReverse':
      return theme.color.spaceGray;

    default:
      return theme.color.white;
  }
}};
`;
const svg = css`
  ${_ref22 => {
  let {
    theme,
    alignIcon
  } = _ref22;
  const dimensions = theme.x3spacePx;
  const margin = theme.x1spacePx / 2;
  const buttonPadding = theme.x1spacePx;
  const padding = alignIcon ? `padding-${alignIcon}: ${dimensions + buttonPadding + margin}px;` : ``;
  return css`
      ${padding}
      > svg {
        display: inline;
        ${svgFill}
        opacity: 0.5;
        width: ${dimensions}px;
        height: ${dimensions}px;
        vertical-align: middle;
        margin: 0 ${alignIcon ? 0 : -(theme.x1spacePx / 2)}px;
        ${alignIcon && css`
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          ${alignIcon}: ${buttonPadding}px;
        `}
      }
    `;
}}
`;
const link = css`
  border-radius: 0;
  text-align: left;
  ${_ref23 => {
  let {
    as: type,
    theme,
    styleType
  } = _ref23;

  if (type === 'a') {
    return `
        display: inline;
        padding: ${styleType === 'softLinkReverse' ? theme.x1spacePx / 2 - 1 : theme.x1spacePx / 2}px 0;
      `;
  }
}}
`;
const buttonCSS = css`
  position: relative;
  transition: 0.2s background-color, 0.2s box-shadow, 0.4s color, padding 0.4s;
  border: 0;
  margin: 0;
  border-radius: 6px;
  text-decoration: none;
  vertical-align: top;
  ${font}
  ${lineHeight}
  ${color}
  ${height}
  ${padding}
  ${boxShadow}
  ${backgroundColor}
  ${media.mdUp`
    &:hover {
      ${hover}
    }
  `}
  &:active {
    ${active}
  }
  &:disabled {
    ${disabled}
    ${_ref24 => {
  let {
    styleType
  } = _ref24;
  return hasDarkSpinner(styleType) ? loadingBlack : loading;
}}
  }
  /*
  * SVG Support
  */
  ${svg}
  /*
  * Spacing
  */
  ${_ref25 => {
  let {
    marginTop,
    marginBottom
  } = _ref25;
  return (marginTop || marginBottom) && verticalMargin;
}}
  ${_ref26 => {
  let {
    marginLeft,
    marginRight
  } = _ref26;
  return (marginLeft || marginRight) && horizontalMargin;
}}
  /*
  * Display
  */
  ${_ref27 => {
  let {
    display,
    displayXsDown,
    displaySmDown,
    displayMdDown,
    displayLgDown,
    displaySmUp,
    displayMdUp,
    displayLgUp,
    displayXlUp
  } = _ref27;
  return (display || displayXsDown || displaySmDown || displayMdDown || displayLgDown || displaySmUp || displayMdUp || displayLgUp || displayXlUp) && box;
}}
  /*
  * Link
  */
  ${_ref28 => {
  let {
    styleType
  } = _ref28;
  return (styleType === 'softLink' || styleType === 'softLinkReverse' || styleType === 'strongLink') && link;
}}
  ${_ref29 => {
  let {
    isLoading,
    display
  } = _ref29;
  return isLoading && !display && css`
      padding-right: 34px;
    `;
}}
`;
const Button = styled('button').attrs(_ref30 => {
  let {
    theme,
    styleType
  } = _ref30;
  //  ✨ Black magic focus ✨
  //  We do this so that we can distinguish between mouse focus and keyboard focus using a .using-mouse class on the body
  //  A non styled-components solution due to scoping
  const focusType = styleType === 'softLink' || styleType === 'strongLink' || styleType === 'softLinkReverse' ? 'solid' : 'dashed';
  return Object.assign({}, theme.focus(focusType));
})`
  ${buttonCSS}
`;

const getWidth = n => n / 12 * 100;
const getFlex = span => {
  if (typeof span !== 'number' || span === undefined) return;
  const n = getWidth(span);
  return css`
    flex: 0 0 ${n}%;
    max-width: ${n}%;
  `;
};
const getFlexOrder = n => {
  if (n === 'first') {
    return `order: -1;`;
  } else if (n === 'last') {
    return `order: 13;`;
  } else {
    return `order: ${n};`;
  }
};
const getFlexOffset = span => {
  if (typeof span !== 'number' || span === undefined) return;
  return `margin-left: ${getWidth(span)}%;`;
};
const getFlexJustifyContent = type => css`
    justify-content: ${type};
  `;
const getFlexAlignContent = type => css`
    align-content: ${type} !important;
  `;
const getFlexAlignItem = type => css`
    align-items: ${type};
  `;
const getFlexAlignSelf = type => css`
    align-self: ${type} !important;
  `;
const getFlexRow = () => css`
    flex-direction: row !important;
  `;
const getFlexColumn = () => css`
    flex-direction: column !important;
  `;
const getFlexRowReverse = () => css`
    flex-direction: row-reverse !important;
  `;
const getFlexColumnReverse = () => css`
    flex-direction: column-reverse !important;
  `;
const getFlexWrap = () => css`
    flex-wrap: wrap !important;
  `;
const getFlexNoWrap = () => css`
    flex-wrap: nowrap !important;
  `;
const getFlexWrapReverse = () => css`
    flex-wrap: wrap-reverse !important;
  `;
const getFlexFillVoid = () => css`
    flex: 1 1 auto !important;
  `;
const getDisplayInline = () => css`
    display: inline !important;
  `;
const getDisplayInlineBlock = () => css`
    display: inline-block !important;
  `;
const getDisplayBlock = () => css`
    display: block !important;
  `;
const getDisplayTable = () => css`
    display: table !important;
  `;
const getDisplayTableRow = () => css`
    display: table-row !important;
  `;
const getDisplayTableCell = () => css`
    display: table-cell !important;
  `;
const getDisplayFlex = () => css`
    display: flex;
  `;
const getDisplayInlineFlex = () => css`
    display: inline-flex !important;
  `;
const getGap = gap => css`
  gap: ${gap};
`;

const Row = styled('div')`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: left;
  ${_ref => {
  let {
    justifyContent
  } = _ref;
  return justifyContent && getFlexJustifyContent(justifyContent);
}}
  ${_ref2 => {
  let {
    alignContent
  } = _ref2;
  return alignContent && getFlexAlignContent(alignContent);
}}
  ${_ref3 => {
  let {
    alignItem
  } = _ref3;
  return alignItem && getFlexAlignItem(alignItem);
}}
  ${_ref4 => {
  let {
    fillVoid
  } = _ref4;
  return fillVoid && getFlexFillVoid();
}}
  ${_ref5 => {
  let {
    row
  } = _ref5;
  return row && getFlexRow();
}}
  ${_ref6 => {
  let {
    column
  } = _ref6;
  return column && getFlexColumn();
}}
  ${_ref7 => {
  let {
    noWrap
  } = _ref7;
  return noWrap && getFlexNoWrap();
}}
  ${_ref8 => {
  let {
    rowReverse
  } = _ref8;
  return rowReverse && getFlexRowReverse();
}}
  ${_ref9 => {
  let {
    columnReverse
  } = _ref9;
  return columnReverse && getFlexColumnReverse();
}}
  ${_ref10 => {
  let {
    gap
  } = _ref10;
  return gap && getGap(gap);
}}  
  ${_ref11 => {
  let {
    width
  } = _ref11;
  return width && css`
      width: ${width};
    `;
}}
  ${_ref12 => {
  let {
    height
  } = _ref12;
  return height && css`
      height: ${height};
    `;
}}
  ${_ref13 => {
  let {
    maxWidth
  } = _ref13;
  return maxWidth && css`
      max-width: ${maxWidth};
    `;
}}
  ${_ref14 => {
  let {
    maxHeight
  } = _ref14;
  return maxHeight && css`
      max-height: ${maxHeight};
    `;
}}

    ${_ref15 => {
  let {
    hiddenXsDown
  } = _ref15;

  if (hiddenXsDown) {
    return media.xsDown`
        ${_ref16 => {
      let {
        hiddenXsDown
      } = _ref16;
      return hiddenXsDown && css`
            display: none !important;
          `;
    }}
      `;
  }
}}

  ${_ref17 => {
  let {
    justifyContentSm,
    alignContentSm,
    rowSm,
    columnSm,
    rowReverseSm,
    columnReverseSm,
    colSm,
    offsetSm,
    orderSm,
    alignItemSm,
    alignSelfSm,
    wrapSm,
    noWrapSm,
    wrapReverseSm,
    fillVoidSm,
    displayInlineSm,
    displayInlineBlockSm,
    displayBlockSm,
    displayTableSm,
    displayTableRowSm,
    displayTableCellSm,
    displayFlexSm,
    displayInlineFlexSm,
    hiddenSmUp
  } = _ref17;

  if (colSm || offsetSm || orderSm || justifyContentSm || alignContentSm || alignItemSm || alignSelfSm || rowSm || columnSm || rowReverseSm || columnReverseSm || wrapSm || noWrapSm || wrapReverseSm || fillVoidSm || displayInlineSm || displayInlineBlockSm || displayBlockSm || displayTableSm || displayTableRowSm || displayTableCellSm || displayFlexSm || displayInlineFlexSm || hiddenSmUp) {
    return media.smUp`
        ${colSm && getFlex(colSm)}
        ${offsetSm && getFlexOffset(offsetSm)}
        ${orderSm && getFlexOrder(orderSm)}
        ${justifyContentSm && getFlexJustifyContent(justifyContentSm)}
        ${alignContentSm && getFlexAlignContent(alignContentSm)}
        ${alignItemSm && getFlexAlignItem(alignItemSm)}
        ${alignSelfSm && getFlexAlignSelf(alignSelfSm)}
        ${rowSm && getFlexRow()}
        ${columnSm && getFlexColumn()}
        ${rowReverseSm && getFlexRowReverse()}
        ${columnReverseSm && getFlexColumnReverse()}
        ${wrapSm && getFlexWrap()}
        ${noWrapSm && getFlexNoWrap()}
        ${wrapReverseSm && getFlexWrapReverse()}
        ${fillVoidSm && getFlexFillVoid()}
        ${displayInlineSm && getDisplayInline()}
        ${displayInlineBlockSm && getDisplayInlineBlock()}
        ${displayBlockSm && getDisplayBlock()}
        ${displayTableSm && getDisplayTable()}
        ${displayTableRowSm && getDisplayTableRow()}
        ${displayTableCellSm && getDisplayTableCell()}
        ${displayFlexSm && getDisplayFlex()}
        ${displayInlineFlexSm && getDisplayInlineFlex()}
        ${hiddenSmUp && css`
            display: none !important;
          `}
      `;
  }
}}

  ${_ref18 => {
  let {
    hiddenSmDown
  } = _ref18;

  if (hiddenSmDown) {
    return media.smDown`
        ${_ref19 => {
      let {
        hiddenSmDown
      } = _ref19;
      return hiddenSmDown && css`
            display: none !important;
          `;
    }}
      `;
  }
}}

  ${_ref20 => {
  let {
    justifyContentMd,
    alignContentMd,
    rowMd,
    columnMd,
    rowReverseMd,
    columnReverseMd,
    colMd,
    offsetMd,
    orderMd,
    alignItemMd,
    alignSelfMd,
    wrapMd,
    noWrapMd,
    wrapReverseMd,
    fillVoidMd,
    displayInlineMd,
    displayInlineBlockMd,
    displayBlockMd,
    displayTableMd,
    displayTableRowMd,
    displayTableCellMd,
    displayFlexMd,
    displayInlineFlexMd,
    hiddenMdUp
  } = _ref20;

  if (colMd || offsetMd || orderMd || justifyContentMd || alignContentMd || alignItemMd || alignSelfMd || rowMd || columnMd || rowReverseMd || columnReverseMd || wrapMd || noWrapMd || wrapReverseMd || fillVoidMd || displayInlineMd || displayInlineBlockMd || displayBlockMd || displayTableMd || displayTableRowMd || displayTableCellMd || displayFlexMd || displayInlineFlexMd || hiddenMdUp) {
    return media.mdUp`
        ${colMd && getFlex(colMd)};
        ${offsetMd && getFlexOffset(offsetMd)}
        ${orderMd && getFlexOrder(orderMd)}
        ${justifyContentMd && getFlexJustifyContent(justifyContentMd)}
        ${alignContentMd && getFlexAlignContent(alignContentMd)}
        ${alignItemMd && getFlexAlignItem(alignItemMd)}
        ${alignSelfMd && getFlexAlignSelf(alignSelfMd)}
        ${rowMd && getFlexRow()}
        ${columnMd && getFlexColumn()}
        ${rowReverseMd && getFlexRowReverse()}
        ${columnReverseMd && getFlexColumnReverse()}
        ${wrapMd && getFlexWrap()}
        ${noWrapMd && getFlexNoWrap()}
        ${wrapReverseMd && getFlexWrapReverse()}
        ${fillVoidMd && getFlexFillVoid()}
        ${displayInlineMd && getDisplayInline()}
        ${displayInlineBlockMd && getDisplayInlineBlock()}
        ${displayBlockMd && getDisplayBlock()}
        ${displayTableMd && getDisplayTable()}
        ${displayTableRowMd && getDisplayTableRow()}
        ${displayTableCellMd && getDisplayTableCell()}
        ${displayFlexMd && getDisplayFlex()}
        ${displayInlineFlexMd && getDisplayInlineFlex()}
        ${hiddenMdUp && css`
            display: none !important;
          `}
      `;
  }
}}

  ${_ref21 => {
  let {
    hiddenMdDown
  } = _ref21;

  if (hiddenMdDown) {
    return media.mdDown`
        ${_ref22 => {
      let {
        hiddenMdDown
      } = _ref22;
      return hiddenMdDown && css`
            display: none !important;
          `;
    }}
      `;
  }
}}

  ${_ref23 => {
  let {
    justifyContentLg,
    alignContentLg,
    rowLg,
    columnLg,
    rowReverseLg,
    columnReverseLg,
    colLg,
    offsetLg,
    orderLg,
    alignItemLg,
    alignSelfLg,
    wrapLg,
    noWrapLg,
    wrapReverseLg,
    fillVoidLg,
    displayInlineLg,
    displayInlineBlockLg,
    displayBlockLg,
    displayTableLg,
    displayTableRowLg,
    displayTableCellLg,
    displayFlexLg,
    displayInlineFlexLg,
    hiddenLgUp
  } = _ref23;

  if (colLg || offsetLg || orderLg || justifyContentLg || alignContentLg || alignItemLg || alignSelfLg || rowLg || columnLg || rowReverseLg || columnReverseLg || wrapLg || noWrapLg || wrapReverseLg || fillVoidLg || displayInlineLg || displayInlineBlockLg || displayBlockLg || displayTableLg || displayTableRowLg || displayTableCellLg || displayFlexLg || displayInlineFlexLg || hiddenLgUp) {
    return media.lgUp`
        ${colLg && getFlex(colLg)};
        ${offsetLg && getFlexOffset(offsetLg)}
        ${orderLg && getFlexOrder(orderLg)}
        ${justifyContentLg && getFlexJustifyContent(justifyContentLg)}
        ${alignContentLg && getFlexAlignContent(alignContentLg)}
        ${alignItemLg && getFlexAlignItem(alignItemLg)}
        ${alignSelfLg && getFlexAlignSelf(alignSelfLg)}
        ${rowLg && getFlexRow()}
        ${columnLg && getFlexColumn()}
        ${rowReverseLg && getFlexRowReverse()}
        ${columnReverseLg && getFlexColumnReverse()}
        ${wrapLg && getFlexWrap()}
        ${noWrapLg && getFlexNoWrap()}
        ${wrapReverseLg && getFlexWrapReverse()}
        ${fillVoidLg && getFlexFillVoid()}
        ${displayInlineLg && getDisplayInline()}
        ${displayInlineBlockLg && getDisplayInlineBlock()}
        ${displayBlockLg && getDisplayBlock()}
        ${displayTableLg && getDisplayTable()}
        ${displayTableRowLg && getDisplayTableRow()}
        ${displayTableCellLg && getDisplayTableCell()}
        ${displayFlexLg && getDisplayFlex()}
        ${displayInlineFlexLg && getDisplayInlineFlex()}
        ${hiddenLgUp && css`
            display: none !important;
          `}
      `;
  }
}}

  ${_ref24 => {
  let {
    hiddenLgDown
  } = _ref24;

  if (hiddenLgDown) {
    return media.lgDown`
        ${hiddenLgDown && css`
            display: none !important;
          `}
      `;
  }
}}

  ${_ref25 => {
  let {
    justifyContentXl,
    alignContentXl,
    rowXl,
    columnXl,
    rowReverseXl,
    columnReverseXl,
    colXl,
    offsetXl,
    orderXl,
    alignItemXl,
    alignSelfXl,
    wrapXl,
    noWrapXl,
    wrapReverseXl,
    fillVoidXl,
    displayInlineXl,
    displayInlineBlockXl,
    displayBlockXl,
    displayTableXl,
    displayTableRowXl,
    displayTableCellXl,
    displayFlexXl,
    displayInlineFlexXl,
    hiddenXlUp
  } = _ref25;

  if (colXl || offsetXl || orderXl || justifyContentXl || alignContentXl || alignItemXl || alignSelfXl || rowXl || columnXl || rowReverseXl || columnReverseXl || wrapXl || noWrapXl || wrapReverseXl || fillVoidXl || displayInlineXl || displayInlineBlockXl || displayBlockXl || displayTableXl || displayTableRowXl || displayTableCellXl || displayFlexXl || displayInlineFlexXl || hiddenXlUp) {
    return media.xlUp`
        ${colXl && getFlex(colXl)};
        ${offsetXl && getFlexOffset(offsetXl)}
        ${orderXl && getFlexOrder(orderXl)}
        ${justifyContentXl && getFlexJustifyContent(justifyContentXl)}
        ${alignContentXl && getFlexAlignContent(alignContentXl)}
        ${alignItemXl && getFlexAlignItem(alignItemXl)}
        ${alignSelfXl && getFlexAlignSelf(alignSelfXl)}
        ${rowXl && getFlexRow()}
        ${columnXl && getFlexColumn()}
        ${rowReverseXl && getFlexRowReverse()}
        ${columnReverseXl && getFlexColumnReverse()}
        ${wrapXl && getFlexWrap()}
        ${noWrapXl && getFlexNoWrap()}
        ${wrapReverseXl && getFlexWrapReverse()}
        ${fillVoidXl && getFlexFillVoid()}
        ${displayInlineXl && getDisplayInline()}
        ${displayInlineBlockXl && getDisplayInlineBlock()}
        ${displayBlockXl && getDisplayBlock()}
        ${displayTableXl && getDisplayTable()}
        ${displayTableRowXl && getDisplayTableRow()}
        ${displayTableCellXl && getDisplayTableCell()}
        ${displayFlexXl && getDisplayFlex()}
        ${displayInlineFlexXl && getDisplayInlineFlex()}
        ${hiddenXlUp && css`
            display: none !important;
          `}
      `;
  }
}}
`;

const Column = styled('div')`
  flex-grow: 1;
  flex-basis: 0;
  position: relative;
  ${_ref => {
  let {
    fillVoid,
    width
  } = _ref;
  return !fillVoid && !width && `width: 100%;`;
}}
  ${_ref2 => {
  let {
    marginRightAuto
  } = _ref2;
  return marginRightAuto && `margin-right: auto;`;
}}
  min-height: 0px;
  max-width: ${_ref3 => {
  let {
    maxWidth
  } = _ref3;
  return maxWidth ? maxWidth : `100%`;
}};
  ${_ref4 => {
  let {
    col,
    width
  } = _ref4;
  return col ? getFlex(col) : width ? `flex: 0 0 ${width};` : `flex: 0 0 100%;`;
}}
  ${_ref5 => {
  let {
    offset
  } = _ref5;
  return offset && getFlexOffset(offset);
}}
  ${_ref6 => {
  let {
    order
  } = _ref6;
  return order && getFlexOrder(order);
}}
  ${_ref7 => {
  let {
    justifyContent
  } = _ref7;
  return justifyContent && getFlexJustifyContent(justifyContent);
}}
  ${_ref8 => {
  let {
    alignContent
  } = _ref8;
  return alignContent && getFlexAlignContent(alignContent);
}}
  ${_ref9 => {
  let {
    alignItem
  } = _ref9;
  return alignItem && getFlexAlignItem(alignItem);
}}
  ${_ref10 => {
  let {
    alignSelf
  } = _ref10;
  return alignSelf && getFlexAlignSelf(alignSelf);
}}
  ${_ref11 => {
  let {
    row
  } = _ref11;
  return row && getFlexRow();
}}
  ${_ref12 => {
  let {
    column
  } = _ref12;
  return column && getFlexColumn();
}}
  ${_ref13 => {
  let {
    rowReverse
  } = _ref13;
  return rowReverse && getFlexRowReverse();
}}
  ${_ref14 => {
  let {
    columnReverse
  } = _ref14;
  return columnReverse && getFlexColumnReverse();
}}
  ${_ref15 => {
  let {
    wrap
  } = _ref15;
  return wrap && getFlexWrap();
}}
  ${_ref16 => {
  let {
    noWrap
  } = _ref16;
  return noWrap && getFlexNoWrap();
}}
  ${_ref17 => {
  let {
    wrapReverse
  } = _ref17;
  return wrapReverse && getFlexWrapReverse();
}}
  ${_ref18 => {
  let {
    fillVoid
  } = _ref18;
  return fillVoid && getFlexFillVoid();
}}
  ${_ref19 => {
  let {
    displayInline
  } = _ref19;
  return displayInline && getDisplayInline();
}}
  ${_ref20 => {
  let {
    displayInlineBlock
  } = _ref20;
  return displayInlineBlock && getDisplayInlineBlock();
}}
  ${_ref21 => {
  let {
    displayBlock
  } = _ref21;
  return displayBlock && getDisplayBlock();
}}
  ${_ref22 => {
  let {
    displayTable
  } = _ref22;
  return displayTable && getDisplayTable();
}}
  ${_ref23 => {
  let {
    displayTableRow
  } = _ref23;
  return displayTableRow && getDisplayTableRow();
}}
  ${_ref24 => {
  let {
    displayTableCell
  } = _ref24;
  return displayTableCell && getDisplayTableCell();
}}
  ${_ref25 => {
  let {
    displayFlex
  } = _ref25;
  return displayFlex && getDisplayFlex();
}}
  ${_ref26 => {
  let {
    displayInlineFlex
  } = _ref26;
  return displayInlineFlex && getDisplayInlineFlex();
}}

  ${_ref27 => {
  let {
    hiddenXsDown
  } = _ref27;

  if (hiddenXsDown) {
    return media.xsDown`
        ${_ref28 => {
      let {
        hiddenXsDown
      } = _ref28;
      return hiddenXsDown && css`
            display: none !important;
          `;
    }}
      `;
  }
}}

  ${_ref29 => {
  let {
    justifyContentSm,
    alignContentSm,
    rowSm,
    columnSm,
    rowReverseSm,
    columnReverseSm,
    colSm,
    offsetSm,
    orderSm,
    alignItemSm,
    alignSelfSm,
    wrapSm,
    noWrapSm,
    wrapReverseSm,
    fillVoidSm,
    displayInlineSm,
    displayInlineBlockSm,
    displayBlockSm,
    displayTableSm,
    displayTableRowSm,
    displayTableCellSm,
    displayFlexSm,
    displayInlineFlexSm,
    hiddenSmUp
  } = _ref29;

  if (colSm || offsetSm || orderSm || justifyContentSm || alignContentSm || alignItemSm || alignSelfSm || rowSm || columnSm || rowReverseSm || columnReverseSm || wrapSm || noWrapSm || wrapReverseSm || fillVoidSm || displayInlineSm || displayInlineBlockSm || displayBlockSm || displayTableSm || displayTableRowSm || displayTableCellSm || displayFlexSm || displayInlineFlexSm || hiddenSmUp) {
    return media.smUp`
        ${colSm && getFlex(colSm)}
        ${offsetSm && getFlexOffset(offsetSm)}
        ${orderSm && getFlexOrder(orderSm)}
        ${justifyContentSm && getFlexJustifyContent(justifyContentSm)}
        ${alignContentSm && getFlexAlignContent(alignContentSm)}
        ${alignItemSm && getFlexAlignItem(alignItemSm)}
        ${alignSelfSm && getFlexAlignSelf(alignSelfSm)}
        ${rowSm && getFlexRow()}
        ${columnSm && getFlexColumn()}
        ${rowReverseSm && getFlexRowReverse()}
        ${columnReverseSm && getFlexColumnReverse()}
        ${wrapSm && getFlexWrap()}
        ${noWrapSm && getFlexNoWrap()}
        ${wrapReverseSm && getFlexWrapReverse()}
        ${fillVoidSm && getFlexFillVoid()}
        ${displayInlineSm && getDisplayInline()}
        ${displayInlineBlockSm && getDisplayInlineBlock()}
        ${displayBlockSm && getDisplayBlock()}
        ${displayTableSm && getDisplayTable()}
        ${displayTableRowSm && getDisplayTableRow()}
        ${displayTableCellSm && getDisplayTableCell()}
        ${displayFlexSm && getDisplayFlex()}
        ${displayInlineFlexSm && getDisplayInlineFlex()}
        ${hiddenSmUp && css`
            display: none !important;
          `}
      `;
  }
}}

  ${_ref30 => {
  let {
    hiddenSmDown
  } = _ref30;

  if (hiddenSmDown) {
    return media.smDown`
        ${_ref31 => {
      let {
        hiddenSmDown
      } = _ref31;
      return hiddenSmDown && css`
            display: none !important;
          `;
    }}
      `;
  }
}}

  ${_ref32 => {
  let {
    justifyContentMd,
    alignContentMd,
    rowMd,
    columnMd,
    rowReverseMd,
    columnReverseMd,
    colMd,
    offsetMd,
    orderMd,
    alignItemMd,
    alignSelfMd,
    wrapMd,
    noWrapMd,
    wrapReverseMd,
    fillVoidMd,
    displayInlineMd,
    displayInlineBlockMd,
    displayBlockMd,
    displayTableMd,
    displayTableRowMd,
    displayTableCellMd,
    displayFlexMd,
    displayInlineFlexMd,
    hiddenMdUp
  } = _ref32;

  if (colMd || offsetMd || orderMd || justifyContentMd || alignContentMd || alignItemMd || alignSelfMd || rowMd || columnMd || rowReverseMd || columnReverseMd || wrapMd || noWrapMd || wrapReverseMd || fillVoidMd || displayInlineMd || displayInlineBlockMd || displayBlockMd || displayTableMd || displayTableRowMd || displayTableCellMd || displayFlexMd || displayInlineFlexMd || hiddenMdUp) {
    return media.mdUp`
        ${colMd && getFlex(colMd)};
        ${offsetMd && getFlexOffset(offsetMd)}
        ${orderMd && getFlexOrder(orderMd)}
        ${justifyContentMd && getFlexJustifyContent(justifyContentMd)}
        ${alignContentMd && getFlexAlignContent(alignContentMd)}
        ${alignItemMd && getFlexAlignItem(alignItemMd)}
        ${alignSelfMd && getFlexAlignSelf(alignSelfMd)}
        ${rowMd && getFlexRow()}
        ${columnMd && getFlexColumn()}
        ${rowReverseMd && getFlexRowReverse()}
        ${columnReverseMd && getFlexColumnReverse()}
        ${wrapMd && getFlexWrap()}
        ${noWrapMd && getFlexNoWrap()}
        ${wrapReverseMd && getFlexWrapReverse()}
        ${fillVoidMd && getFlexFillVoid()}
        ${displayInlineMd && getDisplayInline()}
        ${displayInlineBlockMd && getDisplayInlineBlock()}
        ${displayBlockMd && getDisplayBlock()}
        ${displayTableMd && getDisplayTable()}
        ${displayTableRowMd && getDisplayTableRow()}
        ${displayTableCellMd && getDisplayTableCell()}
        ${displayFlexMd && getDisplayFlex()}
        ${displayInlineFlexMd && getDisplayInlineFlex()}
        ${hiddenMdUp && css`
            display: none !important;
          `}
      `;
  }
}}

  ${_ref33 => {
  let {
    hiddenMdDown
  } = _ref33;

  if (hiddenMdDown) {
    return media.mdDown`
        ${_ref34 => {
      let {
        hiddenMdDown
      } = _ref34;
      return hiddenMdDown && css`
            display: none !important;
          `;
    }}
      `;
  }
}}

  ${_ref35 => {
  let {
    justifyContentLg,
    alignContentLg,
    rowLg,
    columnLg,
    rowReverseLg,
    columnReverseLg,
    colLg,
    offsetLg,
    orderLg,
    alignItemLg,
    alignSelfLg,
    wrapLg,
    noWrapLg,
    wrapReverseLg,
    fillVoidLg,
    displayInlineLg,
    displayInlineBlockLg,
    displayBlockLg,
    displayTableLg,
    displayTableRowLg,
    displayTableCellLg,
    displayFlexLg,
    displayInlineFlexLg,
    hiddenLgUp
  } = _ref35;

  if (colLg || offsetLg || orderLg || justifyContentLg || alignContentLg || alignItemLg || alignSelfLg || rowLg || columnLg || rowReverseLg || columnReverseLg || wrapLg || noWrapLg || wrapReverseLg || fillVoidLg || displayInlineLg || displayInlineBlockLg || displayBlockLg || displayTableLg || displayTableRowLg || displayTableCellLg || displayFlexLg || displayInlineFlexLg || hiddenLgUp) {
    return media.lgUp`
        ${colLg && getFlex(colLg)};
        ${offsetLg && getFlexOffset(offsetLg)}
        ${orderLg && getFlexOrder(orderLg)}
        ${justifyContentLg && getFlexJustifyContent(justifyContentLg)}
        ${alignContentLg && getFlexAlignContent(alignContentLg)}
        ${alignItemLg && getFlexAlignItem(alignItemLg)}
        ${alignSelfLg && getFlexAlignSelf(alignSelfLg)}
        ${rowLg && getFlexRow()}
        ${columnLg && getFlexColumn()}
        ${rowReverseLg && getFlexRowReverse()}
        ${columnReverseLg && getFlexColumnReverse()}
        ${wrapLg && getFlexWrap()}
        ${noWrapLg && getFlexNoWrap()}
        ${wrapReverseLg && getFlexWrapReverse()}
        ${fillVoidLg && getFlexFillVoid()}
        ${displayInlineLg && getDisplayInline()}
        ${displayInlineBlockLg && getDisplayInlineBlock()}
        ${displayBlockLg && getDisplayBlock()}
        ${displayTableLg && getDisplayTable()}
        ${displayTableRowLg && getDisplayTableRow()}
        ${displayTableCellLg && getDisplayTableCell()}
        ${displayFlexLg && getDisplayFlex()}
        ${displayInlineFlexLg && getDisplayInlineFlex()}
        ${hiddenLgUp && css`
            display: none !important;
          `}
      `;
  }
}}

  ${_ref36 => {
  let {
    hiddenLgDown
  } = _ref36;

  if (hiddenLgDown) {
    return media.lgDown`
        ${hiddenLgDown && css`
            display: none !important;
          `}
      `;
  }
}}

  ${_ref37 => {
  let {
    justifyContentXl,
    alignContentXl,
    rowXl,
    columnXl,
    rowReverseXl,
    columnReverseXl,
    colXl,
    offsetXl,
    orderXl,
    alignItemXl,
    alignSelfXl,
    wrapXl,
    noWrapXl,
    wrapReverseXl,
    fillVoidXl,
    displayInlineXl,
    displayInlineBlockXl,
    displayBlockXl,
    displayTableXl,
    displayTableRowXl,
    displayTableCellXl,
    displayFlexXl,
    displayInlineFlexXl,
    hiddenXlUp
  } = _ref37;

  if (colXl || offsetXl || orderXl || justifyContentXl || alignContentXl || alignItemXl || alignSelfXl || rowXl || columnXl || rowReverseXl || columnReverseXl || wrapXl || noWrapXl || wrapReverseXl || fillVoidXl || displayInlineXl || displayInlineBlockXl || displayBlockXl || displayTableXl || displayTableRowXl || displayTableCellXl || displayFlexXl || displayInlineFlexXl || hiddenXlUp) {
    return media.xlUp`
        ${colXl && getFlex(colXl)};
        ${offsetXl && getFlexOffset(offsetXl)}
        ${orderXl && getFlexOrder(orderXl)}
        ${justifyContentXl && getFlexJustifyContent(justifyContentXl)}
        ${alignContentXl && getFlexAlignContent(alignContentXl)}
        ${alignItemXl && getFlexAlignItem(alignItemXl)}
        ${alignSelfXl && getFlexAlignSelf(alignSelfXl)}
        ${rowXl && getFlexRow()}
        ${columnXl && getFlexColumn()}
        ${rowReverseXl && getFlexRowReverse()}
        ${columnReverseXl && getFlexColumnReverse()}
        ${wrapXl && getFlexWrap()}
        ${noWrapXl && getFlexNoWrap()}
        ${wrapReverseXl && getFlexWrapReverse()}
        ${fillVoidXl && getFlexFillVoid()}
        ${displayInlineXl && getDisplayInline()}
        ${displayInlineBlockXl && getDisplayInlineBlock()}
        ${displayBlockXl && getDisplayBlock()}
        ${displayTableXl && getDisplayTable()}
        ${displayTableRowXl && getDisplayTableRow()}
        ${displayTableCellXl && getDisplayTableCell()}
        ${displayFlexXl && getDisplayFlex()}
        ${displayInlineFlexXl && getDisplayInlineFlex()}
        ${hiddenXlUp && css`
            display: none !important;
          `}
      `;
  }
}}
`;

const Wrapper$2 = styled(Row)`
  background: white;
  padding: ${_ref => {
  let {
    theme
  } = _ref;
  return theme.x2spacePx;
}}px;
  border-top: 1px solid #e8e8e8;
  ${media.mdDown`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;

    > button {
      display: flex;
      flex: 1;
      justify-content: center;
      align-items: center;
      &:first-child + button {
        margin-left: ${_ref2 => {
  let {
    theme
  } = _ref2;
  return theme.x1spacePx;
}}px;
      }
    }
  `}
`;

const Footer = _ref => {
  let {
    confirmWithSubmit = false,
    disabled = false,
    secondaryLabel,
    onPrimaryClick,
    onSecondaryClick,
    primaryLabel,
    primaryLoading = false,
    secondaryLoading = false,
    primaryDisabled = false,
    secondaryDisabled = false,
    justifyContent = 'flex-end',
    children
  } = _ref;
  const handleSecondaryClick = useCallback(() => {
    onSecondaryClick && onSecondaryClick();
  }, [onSecondaryClick]);
  const handlePrimaryClick = useCallback(e => {
    confirmWithSubmit === false && onPrimaryClick && onPrimaryClick(e);
  }, [confirmWithSubmit, onPrimaryClick]);
  return jsx(Wrapper$2, {
    as: "footer",
    justifyContentMd: justifyContent || 'flex-end',
    children: children ? children : jsxs(Fragment, {
      children: [typeof secondaryLabel !== 'undefined' && jsx(Button, {
        type: "button",
        "data-testid": "modal-secondary-button",
        styleType: "gentleLargeReverse",
        marginRight: true,
        displaySmDown: "block",
        onClick: handleSecondaryClick,
        disabled: secondaryDisabled || disabled || secondaryLoading,
        isLoading: secondaryLoading,
        "data-cy-id": "modal-close-button",
        children: secondaryLabel
      }), typeof primaryLabel !== 'undefined' && jsx(Button, {
        styleType: "louderLarge",
        "data-testid": "modal-primary-button",
        type: confirmWithSubmit ? 'submit' : 'button',
        displaySmDown: "block",
        disabled: primaryDisabled || disabled || primaryLoading,
        onClick: handlePrimaryClick,
        isLoading: primaryLoading,
        "data-cy-id": "modal-confirm-button",
        children: primaryLabel
      })]
    })
  });
};

const Wrapper$1 = styled('header')`
  ${_ref => {
  let {
    theme
  } = _ref;
  return css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: white;
    position: relative;
    z-index: 1;
    padding: ${theme.x2spacePx}px;
    padding-left: ${theme.x3spacePx}px;
    padding-right: ${theme.x3spacePx}px;
    border-bottom: 1px solid #e8e8e8;
    color: ${theme.color.black};
    min-height: ${theme.x8spacePx}px;
    > h1 {
      ${theme.typography.largeStrong}
      margin: 0;
      color: ${theme.color.spaceGray};
    }
  `;
}}
`;

const Header = _ref => {
  let {
    title,
    onClose,
    disabled,
    hideClose = false
  } = _ref;
  return jsxs(Wrapper$1, {
    "data-cy-id": "modal-header",
    children: [typeof title === 'string' ? jsx("h1", {
      "data-testid": "modal-title",
      children: title
    }) : title, !hideClose && jsx(Button, {
      type: "button",
      styleType: "passiveReverse",
      disabled: disabled,
      "data-testid": "modal-close",
      "data-cy-id": "modal-close-icon-button",
      onClick: onClose,
      children: jsx(CloseIconSVG, {})
    })]
  });
};

const onEnter = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;
const Wrapper = styled(Row)`
  top: 0;
  left: 0;
  position: fixed;
  background: ${_ref => {
  let {
    theme,
    dropOpacity,
    fullscreen,
    isLoading
  } = _ref;
  return fullscreen && !isLoading ? theme.color.white : theme.color.spaceGrayRGBA(dropOpacity ? 0.4 : 0.9);
}};
  animation: ${onEnter} 0.1s linear;
  height: 100%;
  z-index: 9999;
  padding: ${_ref2 => {
  let {
    theme,
    fullscreen
  } = _ref2;
  return fullscreen ? 0 : theme.x2spacePx;
}}px;
  padding-top: ${_ref3 => {
  let {
    theme,
    fullscreen
  } = _ref3;
  return `calc(${fullscreen ? 0 : theme.x2spacePx}px + ${theme.safeAreaInsetTop})`;
}};
  padding-bottom: ${_ref4 => {
  let {
    theme,
    fullscreen
  } = _ref4;
  return `calc(${fullscreen ? 0 : theme.x2spacePx}px + ${theme.safeAreaInsetBottom})`;
}};

  ${_ref5 => {
  let {
    scrollable
  } = _ref5;
  return `overflow-y: ${scrollable ? 'auto' : 'hidden'};`;
}}

  ${media.mdUp`
		padding: ${_ref6 => {
  let {
    theme
  } = _ref6;
  return theme.x4spacePx;
}}px;
  `};

  ${media.smDown`
    ${_ref7 => {
  let {
    scrollable,
    isLoading
  } = _ref7;
  return scrollable && !isLoading && `display: block;`;
}}
	`};

  ${media.lgUp`
    background: ${_ref8 => {
  let {
    theme,
    dropOpacity
  } = _ref8;
  return theme.color.spaceGrayRGBA(dropOpacity ? 0.4 : 0.9);
}};
	`};
`;
const Body = styled('section')`
  position: relative;
  background: white;
  color: black;
  ${_ref9 => {
  let {
    scrollable
  } = _ref9;
  return scrollable && `
    overflow-y: auto;
    overflow-x: hidden;
  `;
}}
  font-size: ${_ref10 => {
  let {
    theme
  } = _ref10;
  return theme.x2space;
}}rem;
  line-height: 1.5;
  padding: ${_ref11 => {
  let {
    theme
  } = _ref11;
  return theme.x4spacePx;
}}px
    ${_ref12 => {
  let {
    theme
  } = _ref12;
  return theme.x3spacePx;
}}px;
  ${media.lgUp`
    max-height: calc(480px - 88px); /* 130px = header height + footer height */
  `};

  ${media.mdDown`
    position: absolute;
    width: 100%;
    z-index: 0;
    top: ${_ref13 => {
  let {
    theme
  } = _ref13;
  return theme.x8spacePx;
}}px;
    /* Height: 100% parent height - header height - footer height*/
    height: ${_ref14 => {
  let {
    theme
  } = _ref14;
  return `calc(100% - ${theme.x8spacePx}px - ${theme.x9spacePx}px)`;
}};
    -webkit-overflow-scrolling: touch;
	`};
`;

const blink = keyframes`
	0% 					{transform: translateY(0px) scaleY(1);}
	90%		 			{transform: translateY(0px) scaleY(1);}
	100% 				{transform: translateY(100px) scaleY(.1);}
`;
const sniff = keyframes`
	0% 					{transform: translateY(0px)}
	80%		 			{transform: translateY(0px)}
	90% 				{transform: translateY(-5px)}
	100% 				{transform: translateY(0px)}
`;
const flap = keyframes`
	0% 					{transform: translateY(0px)}
	80%		 			{transform: translateY(0px)}
	90% 				{transform: translateY(4px)}
	100% 				{transform: translateY(0px)}
`;
const Container = styled.div`
  position: relative;
  z-index: 0;
`;
const Warren = styled.div`
  width: ${props => props.loaderWidth ? props.loaderWidth : 100}px;
  position: relative;
  margin: 0 auto;
  &:after {
    background: rgba(0, 0, 0, 0.2);
    display: block;
    position: absolute;
    content: '';
    width: 88%;
    padding-bottom: 88%;
    border-radius: 100%;
    bottom: 0;
    left: 50%;
    margin-left: -44%;
  }
  .logo {
    width: 100%;
    display: block;
    position: relative;
    z-index: 1;
  }
  .eye {
    animation: 1s linear infinite alternate ${blink};
  }
  .nose {
    animation: 1s linear infinite alternate ${sniff};
  }
  .ear {
    animation: 1s linear infinite alternate ${flap};
  }
`;

const LoadingWarren = _ref => {
  let {
    width = 100
  } = _ref;
  return jsxs(Container, {
    children: [jsx(Warren, {
      loaderWidth: width,
      children: jsxs("svg", {
        className: "logo",
        viewBox: "0 0 480 490",
        version: "1.1",
        xmlns: "http://www.w3.org/2000/svg",
        children: [jsx("rect", {
          fill: "#CCCCCC",
          x: "145",
          y: "335",
          width: "190",
          height: "79"
        }), jsx("path", {
          d: "M240,490 C139.815569,490 56.0108318,419.893841 35,326.08049 L35.0000015,326 L144.96684,255 L240,459 L240,490 Z",
          fill: "#1f2b36"
        }), jsx("path", {
          d: "M445,490 C344.815569,490 261.010832,419.893841 240,326.08049 L240.000002,326 L349.96684,255 L445,459 L445,490 Z",
          fill: "#1f2a35",
          transform: "translate(342.500000, 372.500000) scale(-1, 1) translate(-342.500000, -372.500000) "
        }), jsx("polygon", {
          fill: "#CC3300",
          points: "215.33667 406 240 459 240 335 218 356 234.095032 372.095695"
        }), jsx("polygon", {
          fill: "#A32900",
          transform: "translate(252.331665, 397.000000) scale(-1, 1) translate(-252.331665, -397.000000) ",
          points: "240 406 264.66333 459 264.66333 335 242.66333 356 258.758362 372.095695"
        }), jsx("polygon", {
          fill: "#FFCC00",
          points: "207 401 252 401 252 410 207 410"
        }), jsx("polygon", {
          fill: "#FFFFFF",
          points: "145 255 200 374 240 335"
        }), jsx("polygon", {
          fill: "#FFFFFF",
          transform: "translate(287.500000, 314.500000) scale(-1, 1) translate(-287.500000, -314.500000) ",
          points: "240 255 295 374 335 335"
        }), jsx("path", {
          className: "ear",
          d: "M153.361374,22.1564262 L196.220104,63.0538465 C202.233881,68.7894912 202.462316,78.4086979 196.731243,84.4298614 L155.876685,127.330967 C150.145612,133.349604 140.53619,133.580799 134.522413,127.842627 L91.6649445,86.9464702 C85.6524296,81.2095622 85.4202082,71.5903556 91.1525429,65.5704554 L132.008364,22.6680861 C137.738174,16.6507127 147.348859,16.4195182 153.361374,22.1564262 Z M327.637221,22.1564262 C333.650998,16.4195182 343.261682,16.6507127 348.992755,22.6680861 L389.847314,65.5704554 C395.579649,71.5903556 395.347427,81.2095622 389.336174,86.9464702 L346.477444,127.842627 C340.463667,133.580799 330.855507,133.349604 325.123172,127.330967 L284.267351,84.4298614 C278.537541,78.4086979 278.765976,68.7894912 284.778491,63.0538465 L327.637221,22.1564262 Z",
          fill: "#2E7091"
        }), jsx("polygon", {
          className: "ear",
          fill: "#CCCCCC",
          points: "143.555706 36 105 76.4449398 145.445556 115 184 74.5550602"
        }), jsx("polygon", {
          className: "ear",
          fill: "#CCCCCC",
          points: "338.446202 36 298 74.5550602 336.553798 115 377 76.4449398"
        }), jsx("polygon", {
          className: "ear",
          fill: "#FFFFFF",
          points: "108 79.7731152 144.949473 115 180 78.2268848 143.049258 43"
        }), jsx("polygon", {
          className: "ear",
          fill: "#FFFFFF",
          points: "338.952012 43 302 78.2268848 337.047988 115 374 79.7731152"
        }), jsx("path", {
          d: "M50,175 C107.905087,101.247796 136.858262,64.9996231 136.858262,64.9996231 C157.53134,40.8836231 191.790196,34 240,34 C288.211067,34 322.469924,40.8836231 343.143002,64.9996231 C343.143002,64.9996231 372.096177,101.247796 430,175 C176.666667,174.951927 50,174.951927 50,175 Z",
          fill: "#3885B0"
        }), jsx("path", {
          d: "M50,175 C107.905087,101.247796 136.858262,64.9996231 136.858262,64.9996231 C157.53134,40.8836231 191.790196,34 240,34 C288.211067,34 322.469924,40.8836231 343.143002,64.9996231 C343.143002,64.9996231 372.096177,101.247796 430,175 C176.666667,174.951927 50,174.951927 50,175 Z",
          fill: "#3885B0"
        }), jsx("polygon", {
          fill: "#2E7091",
          points: "50 175 240 335 430 175"
        }), jsx("path", {
          d: "M325,197.093688 C293.16113,136.575275 277.241695,106.317965 277.241695,106.317965 C269.370786,92.1347714 257.124188,92.8590357 240.500634,93.0827613 C223.878349,92.8590357 211.630483,92.1347714 203.760842,106.317965 C203.760842,106.317965 187.840138,136.575275 156,197.093688 C187.840138,233.697475 203.760842,252 203.760842,252 L277.241695,252 C277.241695,252 293.16113,233.697475 325,197.093688 C293.995924,225.730567 252.263864,224.970911 240.500634,192.17678 C228.738674,224.970911 187.005345,225.730567 156,197.093688 C187.840138,233.698739 203.760842,252 203.760842,252 L277.241695,252 C277.241695,252 293.16113,233.698739 325,197.093688 Z",
          fill: "#FFFFFF"
        }), jsx("path", {
          d: "M277.241695,252 L203.760842,252 C203.760842,252 187.840138,233.644658 156,196.931437 C187.005345,225.65294 228.738674,224.891039 240.500634,192 C252.263864,224.891039 293.995924,225.65294 325,196.931437 C293.16113,233.644658 277.241695,252 277.241695,252 Z",
          fill: "#CCCCCC"
        }), jsx("path", {
          className: "nose",
          d: "M182,147.570763 C222.854387,133.476412 256.144357,133.476412 297,147.570763 C279.672936,168.5466 260.508154,183.689679 239.499372,193 C218.493102,183.689679 199.324551,168.5466 182,147.570763 Z",
          fill: "#1A1A1A"
        }), jsx("path", {
          className: "nose",
          d: "M204,146.851558 C229.931226,139.049481 251.06624,139.049481 277,146.851558 C266.001024,158.462161 253.833724,166.844147 240.499366,172 C227.163742,166.844147 214.997709,158.462161 204,146.851558 Z",
          fill: "#4D4D4D"
        }), jsx("polygon", {
          fill: "#0d161d",
          points: "126 267.260864 145 255 240 459 129 348 152 334 120 320"
        }), jsx("polygon", {
          fill: "#0d161d",
          transform: "translate(300.000000, 357.000000) scale(-1, 1) translate(-300.000000, -357.000000) ",
          points: "246 267.260864 265 255 360 459 249 348 272 334 240 320"
        }), jsx("path", {
          d: "M240,468 C244.971277,468 249,472.030345 249,477.000663 C249,481.97098 244.971277,486 240,486",
          fill: "#808080"
        }), jsx("path", {
          d: "M231,468 C235.971277,468 240,472.030345 240,477.000663 C240,481.97098 235.971277,486 231,486",
          fill: "#B3B3B3",
          transform: "translate(235.500000, 477.000000) scale(-1, 1) translate(-235.500000, -477.000000) "
        }), jsx("polygon", {
          fill: "#4D4D4D",
          points: "307 415 380 415 380 425 307 425"
        }), jsx("polygon", {
          fill: "#FFFFFF",
          points: "307 414 336.785868 385 353.67805 401.445258 360.391544 394.908892 380 414"
        }), jsx("path", {
          className: "eye",
          d: "M165.261393,121.363393 C173.509479,121.0088 180.753153,127.584324 183.415542,136 C190.03225,81.4001319 138.524775,79.1257595 127,114.960811 C136.71354,120.769916 147.162974,122.14101 165.261393,121.363393 Z",
          fill: "#1A1A1A"
        }), jsx("path", {
          className: "eye",
          d: "M316.737476,121.363393 C334.835831,122.14101 345.285228,120.769916 355,114.960811 C343.475265,79.1257595 291.966706,81.4001319 298.584657,136 C301.244503,127.584324 308.489418,121.0088 316.737476,121.363393 Z",
          fill: "#1A1A1A"
        })]
      })
    }), jsx(LoadingSpinner, {
      left: "50%",
      bottom: "-16px",
      styleType: "light"
    })]
  });
};

function Content(props) {
  const {
    title,
    asForm,
    isNaked,
    disabled,
    children,
    isLoading,
    scrollable,
    secondaryLabel,
    blurTargetId,
    primaryLabel,
    hideClose,
    fullscreen,
    hideHeader,
    hideFooter,
    primaryLoading,
    secondaryLoading,
    primaryDisabled,
    secondaryDisabled,
    onClose,
    onPrimaryClick,
    onSecondaryClick,
    onTamper,
    'data-cy-id': dataCyId = 'modal-content'
  } = props;
  const handleClose = useCallback(() => {
    if (typeof onClose === 'function') {
      onClose();
    }
  }, [onClose]);
  const handleSecondaryClick = useCallback(() => {
    if (typeof onSecondaryClick === 'function') {
      onSecondaryClick();
    }
  }, [onSecondaryClick]);
  const handlePrimaryClick = useCallback(e => {
    asForm && e.preventDefault();

    if (typeof onPrimaryClick === 'function') {
      onPrimaryClick(e);
    }
  }, [asForm, onPrimaryClick]);
  const tamperedRef = useRef(false);
  useEffect(() => {
    if (typeof blurTargetId === 'undefined') return;
    if (tamperedRef.current) return; // the thing is being shown so bail out

    const intervalObj = setInterval(() => {
      const blurTargetElement = document.getElementById(blurTargetId);

      if (blurTargetElement) {
        const rootStyle = getComputedStyle(blurTargetElement);
        const filterStyle = rootStyle.getPropertyValue('filter');

        if (filterStyle !== 'blur(6px)') {
          clearInterval(intervalObj);

          if (typeof onTamper === 'function') {
            onTamper();
            tamperedRef.current = true;
          }
        }
      } else {
        if (typeof onTamper === 'function') {
          onTamper();
          tamperedRef.current = true;
        }
      }
    }, 2500);
    return () => clearInterval(intervalObj);
  }, [blurTargetId, onTamper]);
  return jsxs(Wrapper, {
    "data-cy-id": dataCyId,
    justifyContent: "center",
    fullscreen: fullscreen,
    isLoading: isLoading,
    scrollable: isNaked,
    dropOpacity: blurTargetId && blurTargetId.length > 0 || false,
    width: "100%",
    children: [isLoading ? jsx(Column, {
      alignSelf: "center",
      children: jsx(LoadingWarren, {
        width: 80
      })
    }) : isNaked ? children : jsxs(Column, {
      maxWidth: "700px",
      alignSelfLg: "center",
      as: asForm ? 'form' : 'section',
      onSubmit: asForm ? handlePrimaryClick : undefined,
      children: [hideHeader === false && jsx(Header, {
        title: title,
        disabled: disabled,
        hideClose: hideClose,
        onClose: handleClose
      }), jsx(Body, {
        scrollable: scrollable,
        "data-cy-id": "modal-body",
        children: children
      }), hideFooter === false && jsx(Footer, {
        confirmWithSubmit: asForm,
        disabled: disabled,
        secondaryLabel: secondaryLabel,
        primaryLabel: primaryLabel,
        onPrimaryClick: handlePrimaryClick,
        onSecondaryClick: handleSecondaryClick,
        primaryLoading: primaryLoading,
        secondaryLoading: secondaryLoading,
        primaryDisabled: primaryDisabled,
        secondaryDisabled: secondaryDisabled
      })]
    }), blurTargetId && blurTargetId.length > 0 && jsx(BlurFilter, {
      filterId: blurTargetId
    })]
  });
}

const Portal = _ref => {
  let {
    children,
    asForm,
    isServer,
    removeWindowScroll,
    title,
    isNaked,
    scrollable,
    disabled,
    isLoading,
    fullscreen,
    blurTargetId,
    secondaryLabel,
    primaryLabel,
    hideClose,
    primaryLoading,
    secondaryLoading,
    primaryDisabled,
    secondaryDisabled,
    hideHeader,
    hideFooter,
    onTamper,
    onClose,
    onPrimaryClick,
    onSecondaryClick,
    'data-cy-id': dataCyId
  } = _ref;
  const [container, setContainer] = useState(null);
  const oldBodyOverflowY = useRef('auto');
  const oldScrollPos = useRef(0);
  useEffect(() => {
    const handleKeyUp = e => {
      switch (e.key) {
        case 'Enter':
          if (!asForm) {
            onPrimaryClick && onPrimaryClick(e);
          }

          break;

        case 'Escape':
          onClose && onClose();
          break;
      }
    };

    const modalContainer = document.createElement('div');
    modalContainer.setAttribute('id', 'modal-container');
    document.body.appendChild(modalContainer);

    if (removeWindowScroll) {
      oldBodyOverflowY.current = document.body.style['overflow-y'] || 'auto';
      oldScrollPos.current = window.pageYOffset;
      document.body.style['overflow-y'] = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${oldScrollPos.current}px`;
      document.body.style.width = '100%';
    }

    document.addEventListener('keyup', handleKeyUp);
    setContainer(modalContainer); // Cleanup

    return () => {
      if (modalContainer !== null && document.body.contains(modalContainer)) {
        try {
          document.body.removeChild(modalContainer);
        } catch (error) {
          console.log(error);
        }
      }

      document.body.style['overflow-y'] = oldBodyOverflowY.current;
      document.body.style.removeProperty('position');
      document.body.style.removeProperty('top');
      document.body.style.removeProperty('width');
      if (removeWindowScroll) window.scrollTo(0, oldScrollPos.current);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [removeWindowScroll]);
  if (isServer && typeof document === 'undefined') return null;
  if (container === null) return null;
  return createPortal(jsx(Content, {
    "data-cy-id": dataCyId,
    title: title,
    asForm: asForm,
    isNaked: isNaked,
    scrollable: scrollable,
    disabled: disabled,
    isLoading: isLoading,
    fullscreen: fullscreen,
    blurTargetId: blurTargetId,
    secondaryLabel: secondaryLabel,
    primaryLabel: primaryLabel,
    onClose: onClose,
    onPrimaryClick: onPrimaryClick,
    onSecondaryClick: onSecondaryClick,
    hideClose: hideClose,
    primaryLoading: primaryLoading,
    secondaryLoading: secondaryLoading,
    primaryDisabled: primaryDisabled,
    secondaryDisabled: secondaryDisabled,
    hideHeader: hideHeader,
    hideFooter: hideFooter,
    onTamper: onTamper,
    children: children
  }), container);
};

const Modal = _ref => {
  let {
    title = '',
    isServer = false,
    blurTargetId,
    asForm = false,
    isNaked = false,
    disabled = false,
    children = null,
    isLoading = false,
    scrollable = true,
    secondaryLabel,
    primaryLabel,
    hideClose = false,
    hideHeader = false,
    hideFooter = false,
    fullscreen = false,
    removeWindowScroll = true,
    primaryLoading = false,
    secondaryLoading = false,
    primaryDisabled = false,
    secondaryDisabled = false,
    onTamper,
    onClose,
    onSecondaryClick,
    onPrimaryClick,
    'data-cy-id': dataCyId
  } = _ref;
  const handleClose = useCallback(() => {
    if (onClose && typeof onClose === 'function') {
      onClose();
    } else if (onSecondaryClick && typeof onSecondaryClick === 'function') {
      onSecondaryClick();
    } else {
      console.log('Please set a close action or secondary action');
    }
  }, []);
  return jsx(Portal, {
    "data-cy-id": dataCyId,
    isServer: isServer,
    asForm: asForm,
    title: title,
    isNaked: isNaked,
    scrollable: scrollable,
    disabled: disabled,
    isLoading: isLoading,
    fullscreen: fullscreen,
    blurTargetId: blurTargetId,
    secondaryLabel: secondaryLabel,
    primaryLabel: primaryLabel,
    hideClose: hideClose,
    primaryLoading: primaryLoading,
    secondaryLoading: secondaryLoading,
    primaryDisabled: primaryDisabled,
    secondaryDisabled: secondaryDisabled,
    hideHeader: hideHeader,
    hideFooter: hideFooter,
    removeWindowScroll: removeWindowScroll,
    onClose: handleClose,
    onPrimaryClick: onPrimaryClick || handleClose,
    onSecondaryClick: onSecondaryClick || handleClose,
    onTamper: onTamper,
    children: children
  });
};

export { Button as B, Column as C, Footer as F, Header as H, LoadingSpinner as L, Modal as M, Row as R, BlurFilter as a, buttonCSS as b, LoadingWarren as c, Content as d, Body as e, loadingAnimation as l };
